import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type DateInput = Date | string | number | null | undefined;
type DateStyle = "short" | "long";

function toValidDate(value: DateInput): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateOptions(style: DateStyle): Intl.DateTimeFormatOptions {
  if (style === "short") {
    return {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
  }

  return {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
}

export function formatDisplayDate(
  value: DateInput,
  opts?: {
    style?: DateStyle;
    timeZone?: string;
    locale?: string;
  },
): string | null {
  const date = toValidDate(value);
  if (!date) return null;

  const style = opts?.style ?? "long";
  const locale = opts?.locale ?? "en-US";

  return new Intl.DateTimeFormat(locale, {
    ...dateOptions(style),
    ...(opts?.timeZone ? { timeZone: opts.timeZone } : {}),
  }).format(date);
}

export function formatDisplayCalendarDate(
  value: DateInput,
  opts?: {
    style?: DateStyle;
    locale?: string;
  },
): string | null {
  return formatDisplayDate(value, {
    style: opts?.style,
    locale: opts?.locale,
    timeZone: "UTC",
  });
}

export function formatDisplayDateTime(
  value: DateInput,
  opts?: {
    locale?: string;
    timeZone?: string;
  },
): string | null {
  const date = toValidDate(value);
  if (!date) return null;

  return new Intl.DateTimeFormat(opts?.locale ?? "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(opts?.timeZone ? { timeZone: opts.timeZone } : {}),
  }).format(date);
}

export function formatDisplayMonthShort(
  value: DateInput,
  opts?: { locale?: string; timeZone?: string },
): string | null {
  const date = toValidDate(value);
  if (!date) return null;

  return new Intl.DateTimeFormat(opts?.locale ?? "en-US", {
    month: "short",
    ...(opts?.timeZone ? { timeZone: opts.timeZone } : {}),
  }).format(date);
}

export function formatCurrency(
  value: number,
  opts?: { locale?: string; currency?: string },
): string {
  return new Intl.NumberFormat(opts?.locale ?? "en-US", {
    style: "currency",
    currency: opts?.currency ?? "USD",
  }).format(value);
}
